import { supabase, supabaseAnonKey } from '../supabaseClient.js';
import axios from 'axios';

export const renameOrganization = async (oldName, newName, userId, teamId) => {
    const { data: userRole } = await supabase
        .from('team_users')
        .select('role')
        .eq('user_id', userId)
        .eq('team_id', teamId)
        .single();

    if (!userRole || userRole.role !== 'Admin') {
        return { error: { message: 'Only administrators can rename an organization' } };
    }

    const { data: teamData, error: teamError } = await supabase.from('teams').select('*');
    if (teamError) {
        console.error("Error: ", teamError);
        return { error: teamError };
    } 
    const decryptedTeamData = await Promise.all(teamData.map(async (team) => {
        const decryptedName = await supabase.rpc('decrypt_text', { encrypted_data: team.name });
        return { ...team, name: decryptedName.data };
    }));
    if (decryptedTeamData.find(team => team.name === newName)) {
        return { error: { message: 'Organization with this name already exists' } };
    }

    const decryptedOldTeam = decryptedTeamData.find(team => team.name === oldName);
    if (!decryptedOldTeam) {
        return { error: { message: 'Organization not found' } };
    }
    const { data, error } = await supabase.from('teams').update({ name: newName }).eq('id', decryptedOldTeam.id).select();
    if (error) {
        console.error("Error: ", error);
        return { error };
    }
    return { error };
};


export const leaveOrganization = async (userId, orgName) => {
  const { data: team } = await supabase.from('teams').select('*');
  if (!team) {
    return { error: { message: 'Organization not found' } };
  }
  
  const decryptedTeamData = await Promise.all(team.map(async (team) => {
    const decryptedName = await supabase.rpc('decrypt_text', { encrypted_data: team.name });
    return { ...team, name: decryptedName.data };
  }));

  const decryptedTeam = decryptedTeamData.find(team => team.name === orgName);
  if (!decryptedTeam) {
    return { error: { message: 'Organization not found' } };
  }

  const { data: userRole } = await supabase
    .from('team_users')
    .select('role')
    .eq('user_id', userId)
    .eq('team_id', decryptedTeam.id)
    .single();

  const isAdmin = userRole?.role === 'Admin';

  const { data: teamMembers } = await supabase
    .from('team_users')
    .select('user_id, role')
    .eq('team_id', decryptedTeam.id);

  if (!teamMembers) {
    return { error: { message: 'Error fetching team members' } };
  }

  if (isAdmin) {
    const adminCount = teamMembers.filter(member => member.role === 'Admin').length;

    if (teamMembers.length === 1) {
        return { 
          error: { 
            code: 'LAST_MEMBER_ADMIN',
            message: 'You are the last member of this organization. To prevent permanent deletion of the organization, consider adding another member as an admin before leaving.' 
          } 
        };
    }
    
    if (adminCount === 1 && teamMembers.length > 1) {
      return { 
        error: { 
          code: 'LAST_ADMIN',
          message: 'You are the only administrator. Please assign another administrator before leaving.' 
        } 
      };
    }
  }
  
  // If all checks pass, proceed with leaving
  const { error } = await supabase
    .from('team_users')
    .delete()
    .eq('user_id', userId)
    .eq('team_id', decryptedTeam.id);

  return { error };
};

export const createNewOrganization = async (_userId, orgName, locations = []) => {
  const { data, error } = await supabase.rpc('org_create', {
    p_name: orgName, p_locations: locations,
  });
  return { error, teamId: data?.team_id };
};

export const acceptOrganizationInvitation = async (teamId) => {
  return supabase.rpc('org_accept_invitation', { p_team: teamId });
};

export const getOrgRoles = async (allOrgs, userId) => {
    let orgRoles = [];
    for (const org of allOrgs) {
        const { data, error } = await supabase.from('team_users').select('role').eq('team_id', org.id).eq('user_id', userId);
        if (error) {
            return { error };
        }
        orgRoles.push(data[0].role);
    }
    return orgRoles;
};

export const deleteOrganization = async (userId, orgName) => {
  const { data: team } = await supabase.from('teams').select('*');
  if (!team) {
    return { error: { message: 'Organization not found' } };
  }
  const decryptedTeamData = await Promise.all(team.map(async (team) => {
    const decryptedName = await supabase.rpc('decrypt_text', { encrypted_data: team.name });
    return { ...team, name: decryptedName.data };
  }));
  const decryptedTeam = decryptedTeamData.find(team => team.name === orgName);
  if (!decryptedTeam) {
    return { error: { message: 'Organization not found' } };
  }
  const { data: userRole } = await supabase
    .from('team_users')
    .select('role')
    .eq('user_id', userId)
    .eq('team_id', decryptedTeam.id)
    .single();

  if (!userRole || userRole.role !== 'Admin') {
    return { error: { message: 'Only administrators can delete an organization' } };
  }

  const { error: teamError } = await supabase
    .from('teams')
    .delete()
    .eq('id', decryptedTeam.id);

  if (teamError) {
    return { error: teamError };
  }
  return { error: null };
};

export const updateTeamLocations = async (teamId, userId, locations) => {
    // Check admin role
    const { data: userRole } = await supabase
        .from('team_users')
        .select('role')
        .eq('user_id', userId)
        .eq('team_id', teamId)
        .single();

    if (!userRole || userRole.role !== 'Admin') {
        return { error: { message: 'Only administrators can manage locations' } };
    }
    const { error } = await supabase
        .from('teams')
        .update({ locations: locations })
        .eq('id', teamId);
    
    return { error };
};

export const getTeamLocations = async (teamId) => {
    const { data, error } = await supabase
        .from('teams')
        .select('locations')
        .eq('id', teamId)
        .single();
    
    if (error) return { error };
    return { data: data.locations || [] };
};
